/** A timezone-stable calendar date, `YYYY-MM-DD`. */
export type DateKey = string;

/**
 * Normalises a basket item's due date to a timezone-stable `YYYY-MM-DD` key,
 * or `null` if the value is absent/invalid.
 *
 * The previous implementation round-tripped every value through
 * `new Date(value).toISOString().slice(0, 10)`, which reinterprets it in UTC.
 * A browser `Date` at local midnight, or an offset-bearing ISO datetime,
 * serialises to the PREVIOUS/NEXT UTC day for any viewer outside UTC, so the
 * generated `collection_targets` could be anchored to the wrong calendar date
 * — violating the elemis §8 date-anchor contract, and the same timezone class
 * of bug fixed in #475-B's `formatDateOnlyShort`. We preserve the CALENDAR date
 * the user picked in the UI instead of a UTC re-projection:
 *   - string: take the leading `YYYY-MM-DD` verbatim, with no `Date`
 *     construction; `null` when it carries no valid date prefix.
 *   - Date: build the key from LOCAL components (`getFullYear`, `getMonth`,
 *     `getDate`), NOT `toISOString()`; `null` for an invalid `Date`.
 *
 * MOVED VERBATIM (#730) from `services/collectionTargets.ts` — body and
 * docblock unchanged. `todayDateKey` below needs exactly this logic, and
 * duplicating it would risk reintroducing the timezone bug #475-D fixed.
 */
export const toDateKey = (value?: string | Date | null): DateKey | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return null;
        }
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
};

/**
 * The anchor for every period derivation: the caller's LOCAL calendar date.
 * `now` is injectable purely so tests can pin it; production never passes it.
 * elemis anchors instalment N at (its own) request date + N months (#730,
 * probe A) — `paymentOptions.startDate` is irrelevant to elemis and must
 * never be used here.
 */
export const todayDateKey = (now: Date = new Date()): DateKey => {
    const key = toDateKey(now);
    // `toDateKey` returns null only for an invalid Date. `new Date()` is
    // never invalid, so this can only fire from a test passing a bad value —
    // throw rather than casting the null away and letting NaN periods leak
    // out to callers that never expect one.
    if (key === null) {
        throw new TypeError('todayDateKey: invalid Date');
    }
    return key;
};

/**
 * Whole months from `from` to `to`, FLOORED — elemis maps a `by_date` target
 * to a period by flooring (#730, probes G/H). Pure integer arithmetic on the
 * date-key components: no `Date` construction, so no DST, timezone or offset
 * exposure. May return 0 or a negative number (the caller clamps); returns
 * `Number.NaN` for an unparsable key so callers can decline to derive rather
 * than guess.
 *
 *   wholeMonthsBetween('2026-08-03', '2027-05-03') === 9   // exact boundary
 *   wholeMonthsBetween('2026-08-03', '2027-05-15') === 9   // off-grid, floors down
 *   wholeMonthsBetween('2026-08-03', '2027-05-02') === 8   // one day short
 *   wholeMonthsBetween('2026-08-03', '2026-08-15') === 0   // before instalment 1
 *
 * End-of-month anchor behaviour (e.g. an anchor of the 31st against a
 * shorter month) is NOT elemis-probed — see #730 R1. This helper applies the
 * documented rule (day-of-month must be reached) rather than clamping to a
 * short month's last day; if elemis is later found to behave differently,
 * this is the only place the rule needs to change.
 */
export const wholeMonthsBetween = (from: DateKey, to: DateKey): number => {
    const fromMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(from);
    const toMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(to);
    if (!fromMatch || !toMatch) {
        return Number.NaN;
    }

    const fromYear = Number(fromMatch[1]);
    const fromMonth = Number(fromMatch[2]);
    const fromDay = Number(fromMatch[3]);
    const toYear = Number(toMatch[1]);
    const toMonth = Number(toMatch[2]);
    const toDay = Number(toMatch[3]);

    let months = (toYear - fromYear) * 12 + (toMonth - fromMonth);
    if (toDay < fromDay) {
        months -= 1;
    }
    return months;
};

/** Days in a 1-based (year, month). `Date.UTC(y, m, 0)` is the last day of
 *  month `m` — used PURELY as a days-in-month lookup table, never to format
 *  the returned key (the key below is built from integer components, so this
 *  file's no-`toISOString()` timezone invariant is preserved). */
const daysInMonth = (year: number, month: number): number =>
    new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * Is `value` a strictly valid `YYYY-MM-DD` CALENDAR date key?
 *
 * Distinct from every other parser in this module, and deliberately so:
 *   - `toDateKey` PREFIX-matches and does not check the calendar, because it
 *     must accept a full ISO datetime as raw input — `toDateKey('2026-99-99')`
 *     returns `'2026-99-99'` unchanged.
 *   - `wholeMonthsBetween` prefix-matches for the same reason.
 *   - `shiftDateKeyMonths` is end-anchored but NORMALISES rather than
 *     rejecting: an out-of-range month rolls into the next year and an
 *     out-of-range day clamps, so it answers "what is this shifted?", never
 *     "is this real?".
 *
 * So a shape regex alone (`/^\d{4}-\d{2}-\d{2}$/`) does NOT express this:
 * `2026-99-99` and `2026-02-30` both match it and neither is a date. Callers
 * that must FAIL CLOSED on a corrupt key — a value off a redux-persist
 * rehydration or off the wire, where the `DateKey` type is only `string` at
 * runtime — need this predicate, not that regex.
 *
 *   isDateKey('2026-08-05')     === true
 *   isDateKey('2028-02-29')     === true   // leap year
 *   isDateKey('2026-02-29')     === false  // 2026 is not
 *   isDateKey('2026-99-99')     === false  // well-shaped, not a date
 *   isDateKey('2026-08-00')     === false  // day is 1-based
 *   isDateKey('2026-8-5')       === false  // not zero-padded
 *   isDateKey('2026-08-05T00Z') === false  // end-anchored, unlike toDateKey
 *
 * Reuses the `daysInMonth` lookup above rather than restating a leap-year
 * rule. That lookup inherits `Date.UTC`'s legacy mapping of years 0-99 onto
 * 1900-1999, so February in a year before 0100 is checked against the
 * 1900s equivalent. Irrelevant to every caller here — no `DateKey` in this
 * codebase originates anywhere but `todayDateKey()` or an elemis row date —
 * and noted only so the next reader does not have to rediscover it.
 */
// flatpickr parses bare strings with its own dateFormat, so a DateKey handed
// straight to it is misread. Callers pass a local-midnight Date instead.
export const dateKeyToLocalDate = (key?: DateKey | null): Date | undefined => {
    if (!key) return undefined;
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!parts) return undefined;
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
};

export const isDateKey = (value: unknown): value is DateKey => {
    if (typeof value !== 'string') {
        return false;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
        return false;
    }
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1) {
        return false;
    }
    return day <= daysInMonth(Number(match[1]), month);
};

/**
 * Shifts a date key by `deltaMonths` whole months (#727), used to pull the
 * end of the DD collection window forward by `collection_lead_months` before
 * `alignCollectionTargets` runs. Returns `null` for a key that is not
 * EXACTLY `YYYY-MM-DD`, or for a non-integer delta — mirrors
 * `wholeMonthsBetween`'s `NaN` posture so a caller can decline rather than
 * guess. Never throws.
 *
 * End-of-month rule: the day-of-month is kept where the target month has
 * that day, and otherwise CLAMPED to the last day of the target month. The
 * clamp applies to the FINAL target month, not step by step —
 * `shiftDateKeyMonths('2026-05-31', -3)` is `2026-02-28`, not a compounding
 * clamp through March/April.
 *
 *   shiftDateKeyMonths('2026-08-15', -2)  === '2026-06-15'  // happy path
 *   shiftDateKeyMonths('2026-03-31', -1)  === '2026-02-28'  // Feb has 28 days
 *   shiftDateKeyMonths('2028-03-31', -1)  === '2028-02-29'  // leap year
 *   shiftDateKeyMonths('2026-01-31', -1)  === '2025-12-31'  // year rollover, no clamp
 *   shiftDateKeyMonths(key, 0)            === key           // zero delta is the identity
 *
 * Do NOT use `new Date(y, m - 1, d)` for this: JS rolls an out-of-range day
 * FORWARD (`new Date(2026, 1, 31)` is 3 March 2026), silently moving a
 * target LATER — the exact opposite of a lead's intent. This implementation
 * computes purely from integer date-key components instead; `Date.UTC`
 * appears only inside `daysInMonth` as a days-in-month lookup.
 *
 * The clamp is one-directional and safe: it can only REDUCE the shifted
 * day-of-month, and `wholeMonthsBetween` subtracts a month when
 * `toDay < fromDay`, so the derived period after a clamp is always <= the
 * period without one — i.e. it can cost a customer at most one extra month
 * of lead in a month-end edge case, never the reverse. This introduces no
 * NEW dependence on elemis's own end-of-month anchor behaviour (contract §8
 * R1, still unprobed) — see the plan's D3 for the full argument.
 *
 * Not invertible: `shiftDateKeyMonths(shiftDateKeyMonths('2026-03-31', -1), +1)`
 * is `2026-03-28`, not the original `2026-03-31`. Nothing in this codebase
 * relies on inverting a shift.
 */
export const shiftDateKeyMonths = (key: DateKey, deltaMonths: number): DateKey | null => {
    if (!Number.isInteger(deltaMonths)) {
        return null;
    }
    // END-ANCHORED, unlike `toDateKey` and `wholeMonthsBetween` above. Those
    // two prefix-match deliberately (`toDateKey` must accept a full ISO
    // datetime as RAW input and take its leading calendar date). This function
    // takes an already-normalised `DateKey`, so a trailing suffix is not an
    // input it has any reason to accept — `'2027-05-15garbage'` fails closed
    // rather than silently shifting a prefix. Defence-in-depth on the
    // parameter type's promise; not a live defect, since the only caller
    // (`applyCollectionLead`) is fed `byDate` values that
    // `buildCollectionTargets` rebuilt from `toDateKey`'s capture groups.
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!match) {
        return null;
    }
    const [, y, m, d] = match;
    const absolute = Number(y) * 12 + (Number(m) - 1) + deltaMonths; // months since year 0
    const year = Math.floor(absolute / 12);
    const month = absolute - year * 12 + 1; // 1..12
    const day = Math.min(Number(d), daysInMonth(year, month));
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
